<template>
  <div>
    <van-popup v-model:show="model" :close-icon-position="$i18n.locale === 'en' ? 'top-right' : 'top-left'"
               :position="!$device.isDesktop?'bottom':'center'"
               :style="{ width: !$device.isDesktop ? '100%' : '500px', height: 'auto' , borderRadius: '10px'}"
               class="p-4" closeable
               v-bind="$attrs"
               @close="resetForm"
               @opened="onOpened">
      <div class="flex flex-col justify-between h-full">
        <div>
          <slot name="title">
            <div class="font-niceone-medium text-[16px] mb-6">{{ $t('product.reviews.product_evaluation') }}</div>
          </slot>
          <div class="flex flex-col space-y-5 p-2 sm:p-4">
            <div class="flex items-center gap-5">
              <niceone-image :src="product.thumb" height="60px" width="60px"/>
              <div class="flex flex-col space-y-1">
                <h5 class="text-sm">{{ product.name }}</h5>
                <h5 class="text-sm font-niceone-bold">{{ product.price_formatted || product?.price_formated }}</h5>
                <h5 v-if="product.quantity && showReview" class="text-sm">{{
                    $t('checkout.quantity', {quantity: product.quantity})
                  }}</h5>
                <p v-if="product.rating" class="flex gap-4">
                  <span class="text-[#e6b719] font-niceone-medium">{{ product.rating }}</span>
                  <van-rate
                      :model-value="product.rating"
                      :size="16"
                      allow-half
                      color="#ffd21e"
                      readonly
                      void-color="#eee"
                      void-icon="star"
                  />
                </p>
              </div>
            </div>
            <div class="flex justify-between py-3 items-center border-b border-b-[#e1e1e1] border-t border-t-[#e1e1e1]">
              <div class="flex gap-6 items-center">
                <span>{{ $t('product.reviews.add_review') }}</span>
                <van-rate
                    v-model="rating"
                    :readonly="showReview"
                    :size="18"
                    color="#ffd21e"
                    void-color="#eee"
                    void-icon="star"
                />
              </div>
              <span class="text-sm font-niceone-bold">{{ rating }}/5</span>
            </div>
            <niceone-field
                v-model="comment"
                :placeholder="$t('common.your_comment')"
                :readonly="showReview"
                autosize
                class="border border-[#ced4da] rounded-md"
                maxlength="50"
                rows="2"
                show-word-limit
                type="textarea"
            />
            <template v-if="!showReview">
              <p>{{ $t('product.reviews.i_attach_some_pictures') }}</p>
              <van-uploader
                  v-model="fileList"
                  :upload-icon="$publicPath('/images/uploaderIcon.svg')"
                  multiple
              >
                <template #preview-cover="{ file }">
                  <div
                      class="absolute bottom-0 box-border w-full p-1 text-white text-center bg-[rgba(0,0,0,0.3)] van-ellipsis"
                  >
                    {{ file.name }}
                  </div>
                </template>
              </van-uploader>
            </template>
            <van-button :disabled="loading || showReview" :loading="loading" block type="primary"
                        @click="submitReview">
              {{ $t('common.send') }}
            </van-button>
          </div>

        </div>
      </div>
    </van-popup>
  </div>
</template>

<script setup>

import {orderProductReviewMutation, productReviewMutation} from "@/plugins/API/review";
import {NOTIFICATION_ERROR_TYPES} from "@/constants";
import {getSingleProductReview} from "@/plugins/API/order";
import {track} from "@/composables/useTrackEvent";
import {TRACKERS} from "~/constants/events";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  product: {
    type: Object,
    default: {}
  },
  showReview: {
    type: Boolean,
    default: false
  },
  orderPage: {
    type: Boolean,
    default: false
  }
});
const rating = ref(5);
const fileList = ref([]);
const comment = ref('');
const emit = defineEmits(['update:modelValue'])
const model = computed({
  get() {
    return props.modelValue
  },
  set(val) {
    emit('update:modelValue', val)
  }
})
const {isLoading: productReviewLoading, mutateAsync: productReviewMutateAsync} = productReviewMutation();
const {isLoading: orderProductReviewLoading, mutateAsync: orderProductReviewMutateAsync} = orderProductReviewMutation()

const loading = computed(() => productReviewLoading.value || orderProductReviewLoading.value)
const submitReview = () => {
  const data = new FormData();

  data.append('text', comment.value)
  data.append('rating', rating.value)
  fileList.value.forEach((attachment, index) => {
    data.append(`attachment[${index}]`, attachment);
  });

  // For order products review
  if (props.orderPage) {
    data.append('order_product_id', props.product?.order_product_id)
    orderProductReviewMutateAsync(data).then(({data}) => {
      showNotification({
        type: NOTIFICATION_ERROR_TYPES.SUCCESS, messages: data.message
      });
      emit('update:modelValue', false)
    }).catch(() => {
    })
    return
  }

  // For product reviews only
  data.append('product_id', props.product.id)
  productReviewMutateAsync(data).then(({data}) => {
    showNotification({
      type: NOTIFICATION_ERROR_TYPES.SUCCESS, messages: data.message
    });
    track(TRACKERS.PRODUCT_RATING, {
      eventAction: `rating_${rating.value}`,
      product: props.product
    })
    emit('update:modelValue', false)
  }).catch(() => {
  });
};

const {mutateAsync: mutateAsyncSingleProductReview} = getSingleProductReview()
const getProductReview = () => {
  mutateAsyncSingleProductReview(props.product.product_id).then(({data}) => {
    comment.value = data.review?.text
    rating.value = data.review?.rating ?? 0
  });
}
const onOpened = () => {
  if (props.showReview) getProductReview();
}
const resetForm = () => {
  rating.value = 5
  comment.value = ''
}
</script>

<style lang="scss" scoped>

</style>